import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { AdminService } from '../../services/adminService';

/**
 * DAU / WAU / MAU par jour. Un actif = un joueur (enfant compris, compté pour
 * lui-même) qui a TERMINÉ une partie ce jour-là ; la définition fait foi côté
 * serveur (`admin.queries` → `getJoueursActifsParJour`), cet écran ne recalcule rien.
 */

// 'YYYY-MM-DD' lu à midi : un minuit UTC basculerait la veille selon le fuseau.
const formatJour = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
const formatJourLong = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR') : '—');

function Chiffre({ titre, valeur, detail, couleur }) {
  return (
    <Card sx={{ height: '100%', borderTop: `4px solid ${couleur}` }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{titre}</Typography>
        <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>{valeur}</Typography>
        {detail && <Typography variant="body2" color="text.secondary">{detail}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function JoueursActifsSection({ segment = 'familles' }) {
  const [jours, setJours] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    AdminService.getActivePlayers(jours, segment)
      .then((d) => { if (!annule) { setData(d); setError(null); } })
      .catch((err) => {
        console.error('Error fetching active players:', err);
        if (!annule) setError('Impossible de charger les joueurs actifs');
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [jours, segment]);

  const serie = data?.serie ?? [];
  const dernier = serie[serie.length - 1];
  // Moyenne du DAU sur 7 jours : le DAU d'un seul jour dépend trop du calendrier.
  const sept = serie.slice(-7);
  const dauMoyen7 = sept.length ? Math.round((sept.reduce((a, r) => a + r.dau, 0) / sept.length) * 10) / 10 : 0;
  const stickiness = dernier?.mau ? Math.round((dauMoyen7 / dernier.mau) * 1000) / 10 : null;

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Joueurs actifs (DAU / WAU / MAU)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Actif = a terminé au moins une partie (combat, aventure, arène…) — une ouverture d'app
        sans partie ne laisse pas de trace. Chaque enfant compte pour lui-même. WAU et MAU :
        joueurs distincts sur les 7 et 30 jours glissants se terminant ce jour-là.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Chiffre
            titre="DAU (moyenne 7 j)"
            valeur={dauMoyen7}
            detail={dernier ? `Aujourd'hui : ${dernier.dau}` : '—'}
            couleur="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Chiffre titre="WAU" valeur={dernier?.wau ?? '—'} detail="7 derniers jours" couleur="#4caf50" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Chiffre titre="MAU" valeur={dernier?.mau ?? '—'} detail="30 derniers jours" couleur="#ff9800" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Chiffre
            titre="DAU / MAU"
            valeur={stickiness === null ? '—' : `${stickiness} %`}
            detail="Fréquence de retour des joueurs du mois"
            couleur="#9c27b0"
          />
        </Grid>
      </Grid>
      <Card>
        <CardHeader
          title="Joueurs actifs par jour"
          avatar={<PeopleIcon color="primary" />}
          action={
            <ToggleButtonGroup
              value={jours}
              exclusive
              size="small"
              onChange={(e, v) => { if (v) setJours(v); }}
              sx={{ pr: 1 }}
            >
              <ToggleButton value={30}>30 j</ToggleButton>
              <ToggleButton value={90}>90 j</ToggleButton>
              <ToggleButton value={180}>180 j</ToggleButton>
              <ToggleButton value={365}>1 an</ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ height: 380, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="jour"
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatJour}
                    angle={-45}
                    textAnchor="end"
                    height={50}
                    minTickGap={10}
                  />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip labelFormatter={formatJourLong} />
                  <Legend />
                  <Line type="monotone" dataKey="dau" name="DAU" stroke="#2196f3" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="wau" name="WAU (7 j)" stroke="#4caf50" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="mau" name="MAU (30 j)" stroke="#ff9800" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
